import { useState } from 'react'
import { FaCheck, FaDownload, FaEye, FaFileAlt, FaFolder, FaLock, FaPlay, FaShareAlt, FaTrashAlt, FaUserLock } from 'react-icons/fa'
import { MdPlaylistAdd } from 'react-icons/md'
import { BeatLoader } from 'react-spinners'
import { formatFileDate, formatFileSize } from '../../../utils/fileBrowserApi'
import { kindOf } from '../fileKinds'
import { VisibilityBadge } from './FileList'

const GRID_COLS = {
  sm: 'grid-cols-[repeat(auto-fill,minmax(120px,1fr))]',
  md: 'grid-cols-[repeat(auto-fill,minmax(170px,1fr))]',
  lg: 'grid-cols-[repeat(auto-fill,minmax(240px,1fr))]',
}

const ICON_SIZE = { sm: 28, md: 36, lg: 48 }

function Thumbnail({ file, thumb, iconSize }) {
  const [failed, setFailed] = useState(false)

  if (thumb?.url && !failed) {
    if (thumb.kind === 'image') {
      return (
        <img
          src={thumb.url}
          alt={file.name}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      )
    }
    return (
      <>
        <video
          src={thumb.url}
          preload="metadata"
          muted
          playsInline
          className="w-full h-full object-cover pointer-events-none"
          onError={() => setFailed(true)}
        />
        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="bg-black/50 rounded-full p-2 text-white">
            <FaPlay size={iconSize / 3} />
          </span>
        </span>
      </>
    )
  }

  // Media file whose signed URL hasn't arrived yet (a failed fetch is
  // cached with url: null and falls through to the icon instead).
  if (!thumb && ['image', 'video'].includes(kindOf(file.name))) {
    return <div className="w-full h-full animate-pulse bg-gray-100" />
  }

  return <FaFileAlt className="text-blue-500" size={iconSize} />
}

export default function FileGrid({
  loading,
  folders,
  files,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  onOpenFolder,
  onView,
  onDownload,
  onShare,
  onAddToQueue,
  onDeleteFile,
  onDeleteFolder,
  onManageAccess,
  isQueued,
  downloadingKey,
  viewingKey,
  thumbs,
  tileSize,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <BeatLoader color={'#2b364b'} />
      </div>
    )
  }

  if (!folders.length && !files.length) {
    return (
      <div className="flex justify-center items-center mt-8">
        <p className="admin-dashboard text-2xl text-gray-500">
          This folder is empty.
        </p>
      </div>
    )
  }

  const allSelected = files.length > 0 && files.every(f => selectedKeys.has(f.key))
  const iconSize = ICON_SIZE[tileSize]

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 px-1">
        <input
          type="checkbox"
          title="Select all (Ctrl+A)"
          className="cursor-pointer accent-[#2b364b]"
          checked={allSelected}
          disabled={!files.length}
          onChange={onToggleSelectAll}
        />
        <span className="text-sm text-gray-600">Select all</span>
      </div>

      <div className={`grid gap-3 select-none ${GRID_COLS[tileSize]}`}>
        {folders.map(folder => (
          <div
            key={folder.prefix}
            className="group relative border border-gray-300 rounded-[10px] bg-white overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onOpenFolder(folder.prefix)}
          >
            <div className="relative aspect-square w-full flex items-center justify-center bg-gray-50">
              <FaFolder className="text-[#8ba3c7]" size={iconSize} />
              {(onManageAccess || onDeleteFolder) && (
                <div className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-3 bg-white/90 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {onManageAccess && (
                    <button
                      type="button"
                      title="Manage access"
                      className="text-gray-500 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-0"
                      onClick={event => {
                        event.stopPropagation()
                        onManageAccess(folder)
                      }}
                    >
                      <FaUserLock size={15} />
                    </button>
                  )}
                  {onDeleteFolder && (
                    <button
                      type="button"
                      title="Delete folder (must be empty)"
                      className="text-gray-500 hover:text-red-600 bg-transparent border-none cursor-pointer p-0"
                      onClick={event => {
                        event.stopPropagation()
                        onDeleteFolder(folder)
                      }}
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium text-gray-800 truncate m-0" title={folder.name}>
                {folder.name}
              </p>
            </div>
          </div>
        ))}

        {files.map(file => {
          const queued = isQueued(file)
          const selected = selectedKeys.has(file.key)
          return (
            <div
              key={file.key}
              className="group relative border border-gray-300 rounded-[10px] bg-white overflow-hidden hover:shadow-md transition-shadow"
            >
              <input
                type="checkbox"
                className={`absolute top-2 left-2 z-10 cursor-pointer accent-[#2b364b] transition-opacity ${
                  selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
                checked={selected}
                onClick={event => event.stopPropagation()}
                onChange={event => onToggleSelect(file, event.nativeEvent.shiftKey)}
              />
              <div
                className="relative aspect-square w-full flex items-center justify-center bg-gray-50 cursor-pointer"
                title={file.key}
                onClick={event => {
                  // Explorer-style: shift+click extends the range, ctrl/cmd+click toggles.
                  if (event.shiftKey || event.ctrlKey || event.metaKey) {
                    onToggleSelect(file, event.shiftKey)
                    return
                  }
                  onView(file)
                }}
              >
                <Thumbnail file={file} thumb={thumbs.get(file.key)} iconSize={iconSize} />
                <div
                  className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-3 bg-white/90 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={event => event.stopPropagation()}
                >
                  {viewingKey === file.key ? (
                    <BeatLoader color="#2b364b" size={5} />
                  ) : (
                    <button
                      type="button"
                      title="View"
                      className="text-gray-600 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-0"
                      onClick={() => onView(file)}
                    >
                      <FaEye size={16} />
                    </button>
                  )}
                  {downloadingKey === file.key ? (
                    <BeatLoader color="#2b364b" size={5} />
                  ) : (
                    <button
                      type="button"
                      title="Download"
                      className="text-gray-600 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-0"
                      onClick={() => onDownload(file)}
                    >
                      <FaDownload size={15} />
                    </button>
                  )}
                  {onShare && (
                    <button
                      type="button"
                      title="Share via email"
                      className="text-gray-600 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-0"
                      onClick={() => onShare(file)}
                    >
                      <FaShareAlt size={15} />
                    </button>
                  )}
                  <button
                    type="button"
                    title={queued ? 'Already in download queue' : 'Add to download queue'}
                    disabled={queued}
                    className={`bg-transparent border-none p-0 ${
                      queued
                        ? 'text-green-600 cursor-default'
                        : 'text-gray-600 hover:text-[#2b364b] cursor-pointer'
                    }`}
                    onClick={() => !queued && onAddToQueue(file)}
                  >
                    {queued ? <FaCheck size={15} /> : <MdPlaylistAdd size={20} />}
                  </button>
                  {onDeleteFile && !file.readOnly && (
                    <button
                      type="button"
                      title="Delete file"
                      className="text-gray-500 hover:text-red-600 bg-transparent border-none cursor-pointer p-0"
                      onClick={() => onDeleteFile(file)}
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="px-2 py-1.5">
                <p className="flex items-center gap-1.5 text-sm text-gray-800 m-0" title={file.key}>
                  <span className="truncate">{file.name}</span>
                  {file.readOnly && (
                    <FaLock className="text-gray-400 shrink-0" size={10} title="Read-only" />
                  )}
                </p>
                {tileSize !== 'sm' && (
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                    <VisibilityBadge visibility={file.visibility} />
                  </div>
                )}
                {tileSize === 'lg' && (
                  <p className="text-xs text-gray-500 truncate m-0 mt-1">
                    {formatFileDate(file.lastModified)}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
